import type { ReactNode } from 'react';
import { CalendarDays, Check, MoreHorizontal, UserPlus } from 'lucide-react';
import { Avatar } from '@/components/apps/Avatar';
import { StatusPicker } from '@/components/apps/StatusPicker';
import { LabelChip } from '@/components/apps/AppLabelsField';
import type { AppLabel } from '@/lib/app-collaboration';
import type { AppCollection, AppMember, AppRecord, AppRecordChanges } from '@/lib/app-records';
import { usePopover } from './usePopover';

const pill = 'inline-flex items-center gap-1.5 rounded-full border border-domino-cardBorder bg-domino-white px-2.5 py-0.5 text-xs text-domino-ink transition hover:border-domino-aubergine';

const dueLabel = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const AssigneePicker = ({ members, value, onChange }: { members: AppMember[]; value: string | null; onChange: (userId: string | null) => void }) => {
  const { open, setOpen, ref } = usePopover();
  const current = members.find((member) => member.userId === value) ?? null;
  const pick = (userId: string | null) => { setOpen(false); if (userId !== value) onChange(userId); };
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)} className={pill}
        aria-label={current ? `Assignée à ${current.name}` : 'Assigner'}>
        {current ? <><Avatar name={current.name} size="xs" className="-ml-1.5" />{current.name}</> : <><UserPlus className="h-3.5 w-3.5" aria-hidden="true" />Assigner</>}
      </button>
      {open && (
        <ul role="listbox" aria-label="Assigner à" className="absolute left-0 z-20 mt-1 max-h-72 min-w-[12rem] overflow-y-auto rounded-xl border border-domino-cardBorder bg-domino-white p-1 shadow-lg">
          {[...(current ? [{ userId: '', name: 'Personne' }] : []), ...members].map((member) => (
            <li key={member.userId || 'none'} role="option" aria-selected={member.userId === (value ?? '')}>
              <button type="button" onClick={() => pick(member.userId || null)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
                {member.userId ? <Avatar name={member.name} size="xs" /> : <span className="h-5 w-5" />}
                <span className="flex-1">{member.name}</span>
                {member.userId === value && <Check className="h-3.5 w-3.5 text-domino-aubergine" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface AppRecordBarProps {
  record: AppRecord;
  collection: AppCollection;
  members: AppMember[];
  labels: AppLabel[];
  onChange: (changes: AppRecordChanges) => Promise<void>;
  /** What the « … » menu holds: due date, labels, folder, archive. */
  menu: ReactNode;
  /** S1: the tools proposed on the object, before « … ». */
  tools?: ReactNode;
}

/** One line above the object: status, who has it, and only what was set; the rest is in « … ». */
export const AppRecordBar = ({ record, collection, members, labels, onChange, menu, tools }: AppRecordBarProps) => {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatusPicker statuses={collection.statuses} value={record.status} onChange={(status) => void onChange({ status })} />
      <AssigneePicker members={members} value={record.assigneeUserId} onChange={(assigneeUserId) => void onChange({ assigneeUserId })} />
      {record.dueDate && (
        <span className="inline-flex items-center gap-1 text-xs text-domino-ink">
          <CalendarDays className="h-3.5 w-3.5 text-domino-inkSoft" aria-hidden="true" />{dueLabel(record.dueDate)}
        </span>
      )}
      {labels.map((label) => <LabelChip key={label.id} label={label} />)}
      {record.archivedAt && <span className="rounded-full bg-domino-muted px-2 py-0.5 text-xs text-domino-inkSoft">Archivée</span>}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
      {tools}
      <div ref={ref} className="relative">
        <button type="button" aria-label="Plus d’options" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink">
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {open && (
          <div role="menu" className="absolute right-0 top-9 z-20 w-80 space-y-4 rounded-2xl border border-domino-cardBorder bg-domino-white p-4 shadow-[var(--domino-shadow-elevated)]">
            {menu}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
